import { Formik } from "formik";
import * as React from "react";
import * as yup from "yup";
import _ from "lodash";
import { toast } from "react-toastify";

import { sentenceCase } from "src/library/utils/string-manipulations";
import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { Button } from "src/library/components/Button";
import {
  Form,
  IFormField
} from "src/library/components/Forms";
import { ButtonLink } from "src/library/components/Link";
import DropInfoSidebar from "src/core/drops/components/DropInfoSidebar";
import {
  InfoBarWrapper,
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { intlPhoneRegex } from "src/utils/validation/regex";
import {
  CreateDropParams,
  CustomData,
  useDropsApi
} from "src/core/drops/api/drops.api";
import { PageProps } from "src/pages/Router/routes";
import {
  IImportSetting,
  ImportOrigin,
  ImportSettingType,
  useImportSettingApi
} from "src/core/shared/api/importSetting.api";
import { validateHeaderMappingField } from "src/library/utils/validation";

interface UrlParams {
  dropId: string;
  customerId: string;
}

const createDropContactDetailsForm: (
  terminology: ITerminology,
  customDataHeaderMappings?: IImportSetting["headerMappings"]
) => IFormField[] = (terminology, customDataHeaderMappings) => {
  let fields: IFormField[] = [
    {
      type: "input",
      name: "branchNumber",
      label: `${terminology.drop.singular.text} Number`,
      placeholder: `${terminology.drop.singular.text} Number`,
      validation: yup
        .string()
        .min(1)
        .required(`${terminology.drop.singular.text} Number is required`)
    },
    {
      type: "input",
      name: "name",
      label: `${terminology.drop.singular.text} Name`,
      placeholder: `${terminology.drop.singular.text} Name`,
      validation: yup
        .string()
        .min(1)
        .required(`${terminology.drop.singular.text} Name is required`)
    },
    {
      type: "fieldset",
      name: "address",
      label: "Address",
      fields: [
        {
          type: "input",
          name: "addressLine1",
          label: "Address line 1",
          placeholder: "Address line 1",
          validation: yup.string().notRequired()
        },
        {
          type: "input",
          name: "addressLine2",
          label: "Address line 2",
          placeholder: "Address line 2",
          validation: yup.string().notRequired()
        },
        {
          type: "input",
          name: "addressLine3",
          label: "Address line 3",
          placeholder: "Address line 3",
          validation: yup.string().notRequired()
        },
        {
          type: "input",
          name: "city",
          label: "City",
          placeholder: "City",
          validation: yup.string().notRequired()
        },
        {
          type: "input",
          name: "postcode",
          label: "Postcode",
          placeholder: "Postcode",
          validation: yup.string().notRequired()
        }
      ]
    },
    {
      type: "fieldset",
      name: "contact",
      label: "Contact Details",
      fields: [
        {
          type: "input",
          name: "contactName",
          label: "Contact Name",
          placeholder: "Contact Name",
          validation: yup.string().notRequired()
        },
        {
          type: "input",
          name: "contactNumber",
          label: "Contact Number",
          placeholder: "Contact Number",
          validation: yup
            .string()
            .matches(intlPhoneRegex, "Please enter a valid telephone number")
            .notRequired()
        },
        {
          type: "input",
          inputType: "email",
          name: "contactEmail",
          label: "Contact Email",
          placeholder: "Contact Email",
          validation: yup
            .string()
            .email()
            .notRequired()
        }
      ]
    }
  ];

  if (customDataHeaderMappings?.length) {
    // The title that will separate custom input fields for normal fields
    const customDataTitle: IFormField = {
      type: "title",
      name: "Custom Fields",
      label: "Custom Fields"
    };

    // Generate custom data fields
    const customDataInputs: IFormField[] = [];

    for (const headerItem of customDataHeaderMappings) {
      // Set default values
      let formFieldConfig: IFormField = {
        type: headerItem.formFieldType || "input",
        inputType: headerItem.type || "text",
        name: headerItem.external.replace(/\s/g, ""),
        label: headerItem.external,
        value: ""
      };

      if (headerItem.validation) {
        formFieldConfig.validation = validateHeaderMappingField(headerItem);
      }

      if (headerItem.options) {
        formFieldConfig = {
          ...formFieldConfig,
          options: headerItem.options.map(o => ({
            value: o,
            text: _.startCase(o)
          })),
          unselectedOption: {
            text: "Please select an option",
            value: ""
          }
        };
      }

      customDataInputs.push(formFieldConfig);
    }

    const customDataFields: IFormField[] = [ customDataTitle, ...customDataInputs ];

    fields = _.concat(fields, customDataFields);
  }

  return fields;
};

/**
 * Edit Drop contact details page
 */
const CreateDropPage: React.ReactFCWithChildren<PageProps<unknown, UrlParams>> = props => {
  const { match, terminology } = props;
  const customerId = match ? parseInt(match.params.customerId, 0) : 0;
  const [ createDropResponse, createDrop ] = useDropsApi("CREATE_DROP");
  const [ customerImportSettingRes, customerImportSettingReq ] = useImportSettingApi("GET_IMPORT_SETTING");
  const formRef = React.useRef<Formik>(null);

  React.useEffect(() => {
    customerImportSettingReq({
      type: ImportSettingType.Customer,
      origin: ImportOrigin.CSV
    });
  }, [ customerImportSettingReq ]);

  const customDataHeaderMappings: IImportSetting["headerMappings"] = React.useMemo(() => {
    if (customerImportSettingRes.data) {
      return customerImportSettingRes.data.headerMappings.filter(item => item.custom);
    }

    return [];
  }, [ customerImportSettingRes.data ]);

  const submitForm = React.useCallback(() => {
    {
      if (formRef.current) {
        formRef.current.submitForm();
      }
    }
  }, []);

  const dropContactDetailsForm = React.useMemo(() => {
    return createDropContactDetailsForm(terminology, customDataHeaderMappings);
  }, [ terminology, customDataHeaderMappings ]);

  React.useEffect(() => {
    // On Success reset the form
    if (createDropResponse.data) {
      // redirect to customers edit page
      props.history.push(`/admin/customers/${customerId}/drops/${createDropResponse.data.id}/defaults`);
    }

    if (createDropResponse.error) {
      toast.error(createDropResponse.error.message);
    }
  }, [
    createDropResponse.data,
    createDropResponse.error,
    customerId,
    props.history
  ]);

  return (
    <Layout
      {...props}
      title={`New ${sentenceCase(terminology.drop.singular.text)}`}
    >
      <MainContentWrapper fill>
        <Form
          ref={formRef}
          error={createDropResponse.error ? createDropResponse.error.message : null}
          submitting={createDropResponse.pending}
          fields={dropContactDetailsForm}
          onSubmit={({
            name,
            addressLine1,
            addressLine2,
            addressLine3,
            city,
            postcode,
            contactName,
            contactNumber,
            contactEmail,
            branchNumber,
            ...customFormData
          }) => {
            const newDrop: CreateDropParams = {
              customerId,
              branchNumber,
              name,
              address: {
                addressLine1,
                addressLine2,
                addressLine3,
                city,
                postcode
              },
              contactName,
              contactNumber,
              contactEmail,
              priceCodeId: 1
            };

            const customData: CustomData = [];

            if (customDataHeaderMappings?.length) {
              for (const customDataItem in customFormData) {
                customData.push({
                  name: customDataItem,
                  value: customFormData[ customDataItem ],
                  description: ""
                });
              }

              newDrop.customData = customData;
            }

            return createDrop(newDrop);
          }}
        />
      </MainContentWrapper>

      <InfoBarWrapper>
        <DropInfoSidebar
          terminology={terminology}
          isLoading={false}
          data={null}
          primaryAction={(
            <ButtonLink
              $buttonType="secondary"
              to={`/admin/customers/${customerId}/drops`}
            >
              Cancel
            </ButtonLink>
          )}
          secondaryAction={(
            <Button
              loading={createDropResponse.pending}
              onClick={submitForm}
            >
              Next
            </Button>
          )}
        />
      </InfoBarWrapper>
    </Layout>
  );
};

export default CreateDropPage;
